import { Box, Button, Stack } from "@mantine/core"
import { useEffect, useRef, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { QuestionForecastCard } from "atlas_forecasting_app/components/forecast/question_forecast_card"
import { EmptyState, ErrorAlert, LoadingState, PageHeader } from "atlas_forecasting_app/components/common/page_elements"
import { RouterLink } from "atlas_forecasting_app/lib/router"

export function FeedPage() {
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const sentinelRef = useRef(null)

  const loadMore = async () => {
    if (loading) return
    setLoading(true)
    try {
      const qs = new URLSearchParams()
      qs.set("limit", "20")
      if (cursor) qs.set("cursor", cursor)
      const data = await apiFetch(`/api/forecasts?${qs.toString()}`)
      setItems((prev) => prev.concat(data.items || []))
      setCursor(data.next_cursor || null)
      setError("")
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMore()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && cursor) loadMore()
      },
      { rootMargin: "600px 0px" },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [cursor, loading])

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow="Public browsing"
        variant="editorial"
        title="Forecast feed"
        subtitle="Public questions with public forecasts, presented as an ongoing research feed."
        action={
          <Button component={RouterLink} to="/new-question" variant="light">
            New question
          </Button>
        }
      />
      <ErrorAlert message={error} />
      {!loading && !error && items.length === 0 ? (
        <EmptyState
          title="No public forecasts yet"
          message="Public forecasts will appear here once Atlas users begin publishing questions and results."
          action={
            <Button component={RouterLink} to="/new-question">
              Create a question
            </Button>
          }
        />
      ) : null}
      <Stack gap="md" className="af-card-list">
        {items.map((item) => (
          <QuestionForecastCard key={item?.question?.id || item?.latest_forecast?.id} item={item} variant="feed" />
        ))}
        <Box ref={sentinelRef}>
          {loading ? <LoadingState label="Loading more forecasts..." /> : null}
        </Box>
      </Stack>
    </Stack>
  )
}
